import clsx from 'clsx';
import React, { useRef } from 'react';

import usePrefersReducedMotion from '@hooks/usePrefersReducedMotion';
import useVideoPlaybackTracking from '@hooks/useVideoPlaybackTracking';

import { backgroundColorValues, marginValues, paddingValues } from '../constants';
import type { VideoComponentProps } from './interfaces';

const Video = ({ config, content, maxWidth }: VideoComponentProps) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  const { handleVideoEnd, handleVideoPause, handleVideoPlay, throttledHandleVideoTimeUpdate } =
    useVideoPlaybackTracking({ src: content.src, videoRef });

  return (
    <figure
      className={clsx(
        'video-block-with-caption relative m-0 w-full *:w-full',
        maxWidth,
        config?.css_classes?.includes('mx-auto') && 'mx-auto'
      )}
    >
      <video
        ref={videoRef}
        {...(config?.autoplay && !prefersReducedMotion && { autoPlay: true })}
        className={clsx(
          'video-block mb-0 mt-24 w-full',
          config?.css_classes,
          !content?.caption && maxWidth,
          backgroundColorValues[config?.background_color],
          marginValues[config?.margin],
          paddingValues[config?.padding]
        )}
        {...((config?.controls || prefersReducedMotion) && { controls: true })}
        {...(config?.loop && !prefersReducedMotion && { loop: true })}
        {...(config?.muted && { muted: true })}
        poster={config?.poster}
        preload={config?.preload}
        {...(config?.playsinline && { playsInline: true })}
        src={content.src}
        style={{ maxWidth: `${config?.width}px` }}
        onEnded={handleVideoEnd}
        onPause={handleVideoPause}
        onPlay={handleVideoPlay}
        onTimeUpdate={throttledHandleVideoTimeUpdate}
      />
      {content?.caption && <figcaption className="text-caption-md">{content.caption}</figcaption>}
    </figure>
  );
};

export default Video;
